import { useMemo, useState } from "react";
import {
  useGetBrandsQuery,
  useDeleteBrandMutation,
} from "../../../../redux/api";
import type { Brand } from "../../../../redux/api";
import {
  IconPlus,
  IconSearch,
  IconEye,
  IconEdit,
  IconTrash,
} from "../../../../components/icons";
import { BrandFormModal } from "./brand";
import { EntityOverview } from "../../../../components/EntityOverview";
import { OverviewFieldList } from "../../../../components/OverviewFieldList";
import { statusTone as overviewStatusTone } from "../../../../utils/status";
import { RowMenu } from "../../../../components/RowMenu";

const COLUMN_COUNT = 4;

const statusTone = (status?: string | null) =>
  (status ?? "").toLowerCase() === "active" ? "badge--success" : "badge--warning";

// Inventory → Brands (list page).
const Brands = () => {
  const {
    data: brands = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetBrandsQuery();

  const [deleteBrand] = useDeleteBrandMutation();

  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const openDetail = (id: string | null) => {
    setEditingId(id);
    setModalOpen(true);
  };

  const [viewingId, setViewingId] = useState<string | null>(null);
  const viewing = brands.find((b) => b.id === viewingId) ?? null;


  const handleDelete = async (brand: Brand) => {
    if (!window.confirm(`Delete "${brand.name}"?`)) return;
    try {
      await deleteBrand(brand.id).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to delete brand.",
      );
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter((b: Brand) =>
      [b.name, b.code, b.website, b.status].some((field) =>
        (field ?? "").toLowerCase().includes(q),
      ),
    );
  }, [brands, search]);

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load brands"
    : null;

  if (viewing) {
    return (
      <>
        <EntityOverview
          backLabel="Brands"
          onBack={() => setViewingId(null)}
          title={viewing.name}
          id={viewing.id}
          status={{ label: viewing.status, tone: overviewStatusTone(viewing.status) }}
          onEdit={() => openDetail(viewing.id)}
          onDelete={() => {
            void handleDelete(viewing).then(() => setViewingId(null));
          }}
          side={
            <div className="overview__image">
              {viewing.logoUrl ? (
                <img src={viewing.logoUrl} alt={viewing.name} />
              ) : (
                <span>No logo</span>
              )}
            </div>
          }
        >
          <OverviewFieldList
            fields={[
              { label: "Code", value: viewing.code },
              { label: "Website", value: viewing.website },
              { label: "Description", value: viewing.description, full: true },
            ]}
          />
        </EntityOverview>
        <BrandFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
      </>
    );
  }

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Brands</h2>
            <p className="panel__sub">
              Showing {visible.length} of {brands.length}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder="Search brands…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => openDetail(null)}
            >
              <IconPlus /> New brand
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading brands…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    {brands.length === 0
                      ? "No brands yet."
                      : "No brands match your search."}
                  </td>
                </tr>
              ) : (
                visible.map((brand: Brand) => (
                  <tr key={brand.id}>
                    <td className="cell-title">
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setViewingId(brand.id)}
                      >
                        {brand.name}
                      </button>
                    </td>
                    <td>
                      {brand.code ? (
                        <span className="pill pill--neutral">{brand.code}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <span className={`badge ${statusTone(brand.status)}`}>
                        <span className="badge__dot" />
                        {brand.status}
                      </span>
                    </td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            label: "View",
                            icon: <IconEye size={16} />,
                            onSelect: () => setViewingId(brand.id),
                          },
                          {
                            label: "Edit",
                            icon: <IconEdit size={16} />,
                            onSelect: () => openDetail(brand.id),
                          },
                          {
                            label: "Delete",
                            icon: <IconTrash size={16} />,
                            onSelect: () => handleDelete(brand),
                            danger: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>


      <BrandFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default Brands;
